import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressBarComponent } from '../../../@commons/@components/progress-bar/progress-bar.component';
import { GddBadgeComponent, badgeAttentionRank } from '../../../@commons/@components/gdd-badge/gdd-badge.component';
import { ThermalConditionBadge } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/thermal-progress.response';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { Router } from '@angular/router';
import RoutesConstants from '../../../@commons/constants/routes.constants';
import { LocationLabelPipe } from '../../../@commons/pipes/location-label.pipe';
import { VwDatePipe } from '../../../@commons/pipes/vw-date.pipe';
import { VwPercentPipe } from '../../../@commons/pipes/vw-number.pipe';
import { ChevronRightIconComponent } from '../../../@icons/chevron-right.component';
import { VwPluralPipe } from '../../../@commons/pipes/vw-plural.pipe';

export interface GddMilestoneItem {
  plantUid: string;
  cultivationUid?: string;
  variantUid?: string;
  cultivationName: string;
  variantName: string;
  locationName?: string;
  currentStepName: string;
  /** Avancement de l'étape, de 0 à 1, selon son horloge (voir `stepClock`). */
  progress: number;
  /** Fin estimée de l'étape (« vers le … ») ; `null` sans estimation, ou pour un compte gratuit. */
  endDate: Date | null;
  /** Conditions de croissance ; `null` sans données thermiques : pas de badge. */
  conditionBadge: ThermalConditionBadge | null;
  /** Jours d'affilée sans météo, non comptés : expliqués sous la barre. */
  weatherGapDays?: number;
  /** Plante cyclique : étape finie, la suivante attend sa fenêtre saisonnière. */
  waitingForSeason?: boolean;
  /** Étape qui attend sa saison, et la date d'ouverture de sa fenêtre (« 2027-02-01 »). */
  nextStepName?: string;
  nextStepOpensOn?: string | null;
}

@Component({
  selector: 'vw-gdd-milestones-widget',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ProgressBarComponent, GddBadgeComponent, SharedModule, LocationLabelPipe, VwDatePipe, VwPercentPipe, ChevronRightIconComponent, VwPluralPipe],
  templateUrl: './gdd-milestones-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./gdd-milestones-widget.component.scss'],
})
export class GddMilestonesWidgetComponent {
  @Input() items: GddMilestoneItem[] = [];
  @Input() maxItems: number = 5;

  constructor(private router: Router) {}

  get visibleItems(): GddMilestoneItem[] {
    return [...this.items]
      .sort((a, b) => {
        // Une plante qui attend sa saison n'a rien à surveiller : en dernier.
        if (!!a.waitingForSeason !== !!b.waitingForSeason) return a.waitingForSeason ? 1 : -1;
        // En pause, puis ralenties : les plantes à surveiller passent devant.
        const attention = badgeAttentionRank(a.conditionBadge) - badgeAttentionRank(b.conditionBadge);
        if (attention !== 0) return attention;
        return b.progress - a.progress;
      })
      .slice(0, this.maxItems);
  }

  progressPercent(progress: number): number {
    // Defensive clamp: old API payloads can still carry an unbounded ratio
    return Math.min(100, Math.max(0, Math.round(progress * 100)));
  }

  onPlantClick(item: GddMilestoneItem) {
    // Navigate to the canonical plant-detail route when we have the full path,
    // otherwise fall back to the garden list.
    if (item.cultivationUid && item.variantUid) {
      this.router.navigate([
        '/garden/cultivations',
        item.cultivationUid,
        'variants',
        item.variantUid,
        'plants',
        item.plantUid,
      ]);
    } else {
      this.router.navigate([RoutesConstants.GARDEN_LIST]);
    }
  }
}
